import { jsx as _jsx } from "react/jsx-runtime";
import { useUpdateResultsQuery } from '#pplx/frontend/apps/ask/all-results/hooks/useAllResultsQuery.js';
import { maybeReloadForCfChallenge } from '#pplx/frontend/apps/ask/cloudflare-challenge/cfChallengeReload.js';
import { useCometState, useEntropyBrowser, } from '#pplx/frontend/apps/ask/comet-browser/providers/EntropyBrowserHooks.js';
import { endStreamResponse, handleStreamProgress, isFollowupQuerySource, trackSubmitQuery, } from '#pplx/frontend/apps/ask/events/submitQueryTracking.js';
import { useConnectorNameLists } from '#pplx/frontend/apps/ask/hooks/connectors-hooks/useConnectorNameLists.js';
import { isEphemeralNewThreadPath } from '#pplx/frontend/apps/ask/hooks/stream-hooks/ephemeralThread.js';
import { useAsyncRelatedQueries } from '#pplx/frontend/apps/ask/hooks/stream-hooks/useAsyncRelatedQueries.js';
import { useBackgroundThreadToasts } from '#pplx/frontend/apps/ask/hooks/stream-hooks/useBackgroundThreadToasts.js';
import { usePushNotificationPrompt } from '#pplx/frontend/apps/ask/hooks/stream-hooks/usePushNotificationPrompt.js';
import { markAskLifecycleTerminal, resolveAskLifecycleOutcome, } from '#pplx/frontend/apps/ask/o11y/askLifecycle.js';
import { useInjectSearchResults } from '#pplx/frontend/apps/ask/providers/ask-query-state-provider/hooks/useInjectSearchResults.js';
import { usePerplexityQueryState } from '#pplx/frontend/apps/ask/providers/ask-query-state-provider/hooks/usePerplexityQueryState.js';
import { useResults } from '#pplx/frontend/apps/ask/providers/ResultsContext.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { getCollectionThreadsQueryKeyPrefix, getSidebarProjectThreadsQueryKeyPrefix, } from '#pplx/frontend/apps/ask/queries/query-keys/collectionKeys.js';
import { getAffectedSidebarProjectThreadQueries } from '#pplx/frontend/apps/ask/queries/query-keys/threadKeys.js';
import { getThreadsQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/threadKeys.js';
import { postAskComplete } from '#pplx/frontend/apps/ask/queries/visitor-queries/postAskComplete.js';
import { isSearchPage } from '#pplx/frontend/apps/ask/routing/routes.js';
import { useLocalNotification } from '#pplx/frontend/apps/ask/runtime/useLocalNotification.js';
import { useRefreshModelsOnBudgetNotice } from '#pplx/frontend/apps/ask/runtime/useRefreshModelsOnBudgetNotice.js';
import { useRefreshRateLimits } from '#pplx/frontend/apps/ask/runtime/useRefreshRateLimits.js';
import { useRunNavigationalSearch } from '#pplx/frontend/apps/ask/runtime/useRunNavigationalSearch.js';
import { useDefaultSearchModel } from '#pplx/frontend/apps/ask/search/hooks/useDefaultSearchModel.js';
import { useSearchModeForSearchModel } from '#pplx/frontend/apps/ask/search/hooks/useSearchModeForSearchModel.js';
import { usePreferredSearchModels } from '#pplx/frontend/apps/ask/settings/hooks/usePreferredSearchModels.js';
import { FLOATING_SIGNUP_STATE_CHANGE_EVENT } from '#pplx/frontend/apps/ask/signup/floatingUpsellEvents.js';
import { useSourceLimits } from '#pplx/frontend/apps/ask/sources/hooks/useSourceLimits.js';
import { useInputActions, useUserInput } from '#pplx/frontend/apps/ask/stores/queryInput.js';
import { hasStreamedLLMToken } from '#pplx/frontend/apps/ask/thread-entry-state/llmToken.js';
import { mapPerplexityMessageSearchModeToSearchMode, SearchMode, SearchModel, } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useIsUserLoggedIn } from '#pplx/frontend/auth/userHooks.js';
import { getCometBrowserInfo, isCometBrowser, isCometMobileBrowser, } from '#pplx/frontend/comet/client/utils.js';
import { RedirectReason } from '#pplx/frontend/components/utils/PrivacyUtils.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import { streamEvents } from '#pplx/frontend/stream/events.js';
import { useStreamStore } from '#pplx/frontend/stream/stream.js';
import { useReport } from '#pplx/frontend/stream/useReport.js';
import { isSubmitParams } from '#pplx/frontend/stream/utils.js';
import { isWindowsApp as parseIsWindowsApp } from '#pplx/frontend/user-agent-parser/browser.js';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
// Single-file events: defined here at their only call site rather than in a shared package.
const inlineImageDataReceivedEvent = EVENT_PAYLOAD;
const RestrictedDebugStreamSideEffects = Dynamic(async () => {
    const { DebugStreamSideEffects } = await import('#pplx/frontend/apps/ask/restricted-feature-debug/index.js');
    return { default: DebugStreamSideEffects };
}, { restricted: true });
const DynamicNotificationToastSideEffects = Dynamic(async () => {
    const { NotificationToastSideEffects } = await import('#pplx/frontend/apps/ask/hooks/stream-hooks/useNotificationToasts.js');
    return { default: NotificationToastSideEffects };
});
const NotificationToastSideEffectsGate = () => {
    const isLoggedIn = useIsUserLoggedIn();
    if (!isLoggedIn)
        return null;
    return _jsx(DynamicNotificationToastSideEffects, {});
};
export const StreamSideEffects = React.memo(function StreamSideEffects() {
    useRefreshModelsOnBudgetNotice();
    const reason = 'stream-side-effects';
    const router = useRouter();
    const isComet = isCometBrowser();
    const { track } = useInstrumentation();
    const { $t } = useIntl();
    const isWindowsApp = parseIsWindowsApp(globalThis.navigator?.userAgent);
    const { sendNotification } = useLocalNotification({
        windowsAppOnly: false,
    });
    const injectSearchResults = useInjectSearchResults();
    const runNavigationalSearch = useRunNavigationalSearch();
    const cometAdapter = useEntropyBrowser();
    const isCometMobile = isCometMobileBrowser();
    const { lastResult, inFlight } = useResults();
    const updateResultsQuery = useUpdateResultsQuery();
    const { resetInput, restoreInput } = useInputActions();
    const userInput = useUserInput();
    const userInputRef = useRef(userInput);
    userInputRef.current = userInput;
    const connectors = useConnectorNameLists({ reason });
    const queryClient = useQueryClient();
    const streamStore = useStreamStore();
    const refreshRateLimits = useRefreshRateLimits();
    const { updateLocalStatePostStream } = usePerplexityQueryState();
    const { checkSourceLimits } = useSourceLimits({ enabled: false });
    const cometState = useCometState();
    // hooks for resetting search mode after Model Council queries
    const { getPreferredSearchModel } = usePreferredSearchModels();
    const { configuredModel, setConfiguredModel } = useThreadConfig();
    const defaultSearchModel = useDefaultSearchModel();
    const initializedThreadIdRef = useRef(undefined);
    // Fire analytics event for each stream created
    useLayoutEffect(() => streamEvents.on('created', async ({ analyticsPolicy, stream, query, params }) => {
        // Don't count reconnect as a new stream
        if (stream.isReconnect || !query || !params) {
            return;
        }
        const cometInfo = getCometBrowserInfo();
        trackSubmitQuery(stream.id, {
            analyticsPolicy,
            name: 'perplexity_ask',
            queryStr: query,
            queryParams: params,
            isFollowup: isSubmitParams(params) && isFollowupQuerySource(params.query_source),
            isPerplexityBrowser: isComet,
            frontendUUID: 
            // @ts-expect-error TS(2322): frontend_uuid doesn't exist in the protobuf
            params.frontend_uuid ?? '',
            track,
            browserVersion: cometInfo?.browser_version,
            connectors,
            mentions: 'mentions' in params ? params.mentions : [],
            inlineAssistantAction: cometState.inlineAssistantParams.action,
        });
        cometAdapter?.setTabProgress(true);
        // Send thread status to mobile sidecar when stream starts
        if (isCometMobile) {
            cometAdapter.sendThreadStatusToMobile({
                status: 'working',
                streamId: stream.id.description,
            });
        }
    }), [
        isComet,
        track,
        connectors,
        cometAdapter,
        isCometMobile,
        cometState.inlineAssistantParams.action,
    ]);
    useEffect(() => streamEvents.on('progress', ({ stream, message, isFirstMessage }) => {
        if (isFirstMessage && !stream.isReconnect && message.collection_info) {
            void queryClient.invalidateQueries(getAffectedSidebarProjectThreadQueries({
                projectUUIDs: [message.collection_info.uuid],
            }));
        }
    }), [queryClient]);
    // Restore Comet tab progress when a reconnect resumes an in-flight response
    useLayoutEffect(() => streamEvents.on('progress', ({ stream, message, isFirstMessage }) => {
        if (stream.isReconnect && isFirstMessage && message.reconnectable) {
            cometAdapter?.setTabProgress(true);
        }
    }), [cometAdapter]);
    // Fire analytics event for each stream progress
    useLayoutEffect(() => streamEvents.on('progress', ({ stream, message }) => {
        const { blocks, telemetry_data } = message;
        // handle blocks-based streaming
        // analytics logging based on blocks
        const hasLLMToken = hasStreamedLLMToken(message);
        const hasSearchResults = blocks?.some((block) => !!block?.web_result_block?.web_results?.length) ?? false;
        const hasSources = blocks?.some((block) => !!block?.sources_mode_block?.rows?.length) ?? false;
        if (telemetry_data) {
            const { has_displayed_search_results, has_first_output_token, has_answer_candidate, has_first_token, has_widget_data, has_nav_results, early_nav_v3_call, first_widget_type, country, is_followup, source, engine_mode, search_implementation_mode, has_useful_renderable_content, region, } = telemetry_data;
            const extras = {
                country,
                is_followup,
                source,
                engine_mode,
                search_implementation_mode,
                region,
            };
            if (has_displayed_search_results) {
                stream.timer.addTimingOnce('web.frontend.first_search_results_latency_ms', extras);
            }
            // Candidate before committed: `addTimingOnce` stamps at call time, and a
            // payload can first carry both, so this keeps the recorded order matching
            // the lane order the Debugger renders.
            if (has_answer_candidate) {
                stream.timer.addTimingOnce('web.frontend.first_answer_candidate_latency_ms', extras);
            }
            if (has_first_output_token) {
                stream.timer.addTimingOnce('web.frontend.first_output_token_latency_ms', extras);
            }
            if (has_first_token) {
                stream.timer.addTimingOnce('web.frontend.first_token_latency_ms', extras);
            }
            if (has_widget_data) {
                stream.timer.addTimingOnce('web.frontend.first_widget_data_latency_ms', {
                    ...extras,
                    first_widget_type,
                });
            }
            if (has_useful_renderable_content) {
                stream.timer.addTimingOnce('web.frontend.first_useful_renderable_content_latency_ms', extras);
            }
            if (has_nav_results) {
                stream.timer.addTimingOnce('web.frontend.first_has_nav_results_latency_ms', extras);
            }
            if (early_nav_v3_call) {
                stream.timer.addTimingOnce('web.frontend.early_nav_v3_call_latency_ms', extras);
            }
        }
        handleStreamProgress(stream.id, { hasLLMToken, hasSearchResults, hasSources }, track);
    }), [track]);
    // Inputs clear the visible value on query submit but not the
    // session storage. Reset the input (and storage) when a stream
    // received its first message as it's an indicator that the
    // query was successful and we definitely don't need the last
    // input value. However, just clearing the store would create an
    // issue if someone rapidly typed while the query was starting
    // streaming--so we only reset if the input is empty.
    // Note: technically we should be more specific about which
    // stream message since we can have multiple, but it's unlikely
    // we will have multiple in <700ms, so this should be sufficient.
    useLayoutEffect(() => streamEvents.on('progress', ({ stream, isFirstMessage }) => {
        if (isFirstMessage &&
            stream.placeholderChunk &&
            stream.placeholderChunk.query_source &&
            !userInputRef.current) {
            resetInput();
        }
    }), [resetInput]);
    // When navigating to an existing deep research thread, initialize the
    // configured model from the thread's last result so the followup input
    // reflects the correct mode.
    useEffect(() => {
        if (!lastResult?.context_uuid) {
            initializedThreadIdRef.current = undefined;
            return;
        }
        if (inFlight) {
            return;
        }
        const threadId = lastResult.context_uuid;
        if (initializedThreadIdRef.current === threadId) {
            return;
        }
        initializedThreadIdRef.current = threadId;
        const searchMode = mapPerplexityMessageSearchModeToSearchMode(lastResult.search_mode);
        if (searchMode !== SearchMode.RESEARCH) {
            return;
        }
        const displayModel = lastResult.display_model;
        if (displayModel && displayModel !== configuredModel) {
            setConfiguredModel(displayModel);
        }
    }, [lastResult, inFlight, configuredModel, setConfiguredModel]);
    // Log the actual error when a stream error event fires
    useEffect(() => streamEvents.on('error', ({ error }) => {
        logger.error(error);
    }), []);
    // On a Cloudflare 403 ask-SSE challenge, navigate to a cache-busting
    // /challenge URL so the interstitial gets a chance to render. A non-zero
    // Eppo value enables the feature; cfChallengeReload.ts caps navigations per
    // session. This is a layout effect so it subscribes (and therefore runs)
    // before the ephemeral-URL recovery handler below: it reads the pristine
    // location and its hard navigation supersedes that handler's soft
    // router.replace. The current URL may be an ephemeral /search/new/{uuid}
    // that the backend never created (the /ask SSE that would persist it was
    // blocked), so in that case return the user home — the typed query is kept
    // in sessionStorage and the home input restores it.
    useLayoutEffect(() => streamEvents.on('error', ({ error, stream }) => {
        const { pathname, search, hash } = window.location;
        const returnUrl = isEphemeralNewThreadPath(pathname, stream.id.description)
            ? '/'
            : `${pathname}${search}${hash}`;
        maybeReloadForCfChallenge(error, true, returnUrl);
    }), []);
    // Fire analytics event for each stream completed
    useLayoutEffect(() => streamEvents.on('completed', ({ stream }) => {
        endStreamResponse(stream.id, {
            name: 'SUCCESSFUL response',
            track,
        });
    }), [track]);
    useLayoutEffect(() => {
        const entryContextUuid = (stream) => stream.entryId ? streamStore.getState().entries[stream.entryId]?.context_uuid : undefined;
        const unsubscribers = [
            streamEvents.on('completed', ({ stream, message }) => {
                if (stream.abortController?.signal.aborted) {
                    return;
                }
                markAskLifecycleTerminal(stream, resolveAskLifecycleOutcome('completed', message), entryContextUuid(stream));
            }),
            streamEvents.on('error', ({ stream, message }) => markAskLifecycleTerminal(stream, resolveAskLifecycleOutcome('error', message), entryContextUuid(stream))),
            streamEvents.on('aborted', ({ stream }) => markAskLifecycleTerminal(stream, resolveAskLifecycleOutcome('aborted'), entryContextUuid(stream))),
        ];
        return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
    }, [streamStore]);
    useLayoutEffect(() => {
        const onEvent = ({ thread, message }) => {
            if (message) {
                updateResultsQuery(message, thread);
            }
        };
        const unsubFunctions = [
            streamEvents.on('progress', onEvent),
            streamEvents.on('completed', onEvent),
            streamEvents.on('error', onEvent),
            streamEvents.on('aborted', onEvent),
        ];
        return () => {
            unsubFunctions.forEach((unsub) => unsub());
        };
    }, [updateResultsQuery]);
    const getSearchModeForSearchModel = useSearchModeForSearchModel();
    // Trigger final response side effects
    useLayoutEffect(() => streamEvents.on('completed', ({ stream, thread }) => {
        if (!stream.threadId || !stream.entryId) {
            return;
        }
        const lastResponse = thread?.find((item) => item?.uuid === stream.id.description || item?.backend_uuid === stream.entryId);
        let notificationText = undefined;
        if (getSearchModeForSearchModel(lastResponse?.display_model) === SearchMode.RESEARCH) {
            notificationText = $t({
                defaultMessage: 'Your report is ready',
                description: 'Browser notification shown when a deep research report finishes generating',
                id: '7b03b053e2',
            });
        }
        triggerFinalResponseSideEffects({
            lastResponse: lastResponse,
            backend_uuid: stream.entryId,
            track,
            sendNotification,
            notificationText,
            cometAdapter,
            getSearchModeForSearchModel,
            getPreferredSearchModel,
            setConfiguredModel,
            defaultSearchModel,
        });
    }), [
        $t,
        track,
        sendNotification,
        cometAdapter,
        getSearchModeForSearchModel,
        getPreferredSearchModel,
        setConfiguredModel,
        defaultSearchModel,
    ]);
    // Replace URL w/ the slug
    useLayoutEffect(() => {
        const onEvent = ({ stream }) => {
            const { id } = stream;
            endStreamResponse(id, {
                name: 'FAILED response',
                track,
            });
            if (isEphemeralNewThreadPath(window.location.pathname, id.description)) {
                // Restore the input so user can retry
                restoreInput();
                // Don't leave someone on a broken ephemeral stream url
                router.replace(`/?error=${RedirectReason.ThreadStreamError}`, 'StreamSideEffects');
            }
            cometAdapter?.setTabProgress(false);
        };
        const onErrorUnsubscribe = streamEvents.on('error', onEvent);
        const onAbortedUnsubscribe = streamEvents.on('aborted', onEvent);
        return () => {
            onErrorUnsubscribe();
            onAbortedUnsubscribe();
        };
    }, [track, restoreInput, router, cometAdapter]);
    // Trigger navigation results when a stream is created
    useLayoutEffect(() => streamEvents.on('created', ({ query, params, stream: { isReconnect, id, timer } }) => {
        if (!query || !params || isReconnect || !isSubmitParams(params)) {
            return;
        }
        if (isComet) {
            // gets navigation results for comet via extension
            injectSearchResults(query, {
                model_preference: params.model_preference,
                last_backend_uuid: 'last_backend_uuid' in params ? params.last_backend_uuid : undefined,
                sources: params.sources,
                override_no_search: params.override_no_search,
            }, id.description);
            return;
        }
        // how web will retrieve nav results on submit
        runNavigationalSearch({
            query,
            params: params,
            reason: 'streamEvents.on(created)',
            streamTimer: timer,
            isComet,
            streamId: id.description,
        });
    }), [injectSearchResults, runNavigationalSearch, isComet]);
    // Cleanup Comet tasks when a stream is completed, aborted, or in error
    useEffect(() => {
        if (!isComet)
            return;
        const unsubFunctions = [
            streamEvents.on('completed', ({ stream, message }) => {
                // A reconnectable completion is a handoff to reconnect, not the end of the response.
                if (message?.reconnectable)
                    return;
                cometAdapter.cleanupTasks(stream.entryId, 'stream_completed');
                if (isCometMobile) {
                    cometAdapter.sendThreadStatusToMobile({
                        status: 'ready',
                        streamId: stream.id.description,
                    });
                }
            }),
            streamEvents.on('aborted', ({ stream }) => {
                cometAdapter.cleanupTasks(stream.entryId, 'stream_aborted');
                if (isCometMobile) {
                    cometAdapter.sendThreadStatusToMobile({
                        status: 'ready',
                        streamId: stream.id.description,
                    });
                }
            }),
            // we're not clearing agent tasks here, assuming that frontend will be
            // "aborted" or "completed" at some point after reconnects
            // the only exception - is stream is already aborted or not reconnectable
            streamEvents.on('error', ({ stream, message }) => {
                if ((message && !message.reconnectable) || stream.abortController?.signal.aborted) {
                    cometAdapter.cleanupTasks(stream.entryId, 'stream_error');
                }
                if (isCometMobile) {
                    cometAdapter.sendThreadStatusToMobile({
                        status: 'error',
                        streamId: stream.id.description,
                    });
                }
            }),
        ];
        return () => {
            unsubFunctions.forEach((unsub) => unsub());
        };
    }, [cometAdapter, isCometMobile, isComet]);
    // Misc post-stream side effects
    useEffect(() => streamEvents.on('completed', async ({ message, thread }) => {
        postAskComplete();
        if (!message) {
            return;
        }
        updateLocalStatePostStream(message);
        // Invalidate cache for collection threads (all filter variants)
        if (message.collection_info?.slug) {
            void queryClient.invalidateQueries(getAffectedSidebarProjectThreadQueries({
                projectUUIDs: [message.collection_info.uuid],
            }));
            queryClient.invalidateQueries({
                queryKey: getSidebarProjectThreadsQueryKeyPrefix(message.collection_info.slug),
            });
            queryClient.invalidateQueries({
                queryKey: getCollectionThreadsQueryKeyPrefix(message.collection_info.slug),
                exact: false,
            });
        }
        refreshRateLimits();
        checkSourceLimits();
        // The side effects below don't apply if we're navigated away from
        // the search page for this message
        if (isSearchPage(message.thread_url_slug)) {
            return;
        }
        const fork = Boolean(message.parent_info);
        const isFollowup = thread.findIndex((item) => item.uuid === message.uuid) > 0;
        if (fork || !isFollowup) {
            window.dispatchEvent(new CustomEvent(FLOATING_SIGNUP_STATE_CHANGE_EVENT));
            // also invalidate any loaded library list cache
            queryClient.invalidateQueries({
                queryKey: getThreadsQueryKey(),
                refetchType: 'all',
            });
        }
    }), [queryClient, isWindowsApp, refreshRateLimits, updateLocalStatePostStream, checkSourceLimits]);
    // Clear tab progress when page is about to unload to prevent the tab
    // from showing a glow effect after navigation away from the page
    useEffect(() => {
        const handleBeforeUnload = () => {
            cometAdapter?.setTabProgress(false);
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [cometAdapter]);
    useBackgroundThreadToasts();
    usePushNotificationPrompt();
    useAsyncRelatedQueries();
    useReport();
    return _jsx(NotificationToastSideEffectsGate, {});
});
export const DebugStreamSideEffects = React.memo(function DebugStreamSideEffectsProvider() {
    const { navigationMeta } = useCometState();
    return _jsx(RestrictedDebugStreamSideEffects, { navigationMeta: navigationMeta });
});
function triggerFinalResponseSideEffects({ lastResponse, backend_uuid, track, notificationText, sendNotification, cometAdapter, getSearchModeForSearchModel, getPreferredSearchModel, setConfiguredModel, defaultSearchModel, }) {
    if (!backend_uuid) {
        logger.error(new Error('No backend_uuid found in response'));
        return false;
    }
    if (!lastResponse) {
        logger.error(new Error('No last response found in query client'));
        return false;
    }
    // handle notification for deep research responses
    if (getSearchModeForSearchModel(lastResponse.display_model) === SearchMode.RESEARCH &&
        notificationText &&
        sendNotification) {
        if (document.visibilityState !== 'visible') {
            sendNotification({
                title: notificationText,
                body: lastResponse.query_str,
                onClick: () => {
                    window.focus();
                },
            });
        }
    }
    if (lastResponse.blocks) {
        lastResponse.blocks
            .filter((block) => block.intended_usage?.endsWith('_markdown'))
            .forEach((block) => {
            if (block.markdown_block?.media_items && track) {
                block.markdown_block.media_items.forEach((mediaItem) => {
                    track(inlineImageDataReceivedEvent({
                        name: 'inline image data received',
                        data: {
                            image_url: mediaItem.image ?? '',
                            entryUUID: backend_uuid,
                        },
                    }));
                });
            }
        });
    }
    // A reconnectable completion is a handoff to reconnect, not the end of the response.
    if (!lastResponse.reconnectable) {
        cometAdapter?.setTabProgress(false);
        cometAdapter?.showNotification();
    }
    // reset search mode after Model Council (AGENTIC_RESEARCH) query completes
    // this ensures users return to their preferred SEARCH mode after using Model Council
    // note: we check search_mode field directly since display_model may be individual comparison models
    if (lastResponse.search_mode === 'AGENTIC_RESEARCH' && setConfiguredModel) {
        const preferredSearchModel = getPreferredSearchModel?.(SearchMode.SEARCH);
        const modelToRestore = (preferredSearchModel ?? defaultSearchModel);
        setConfiguredModel(modelToRestore);
    }
    return true;
}
//# sourceMappingURL=StreamSideEffects.js.map